'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import type { MinimalWave } from '@/contexts/wave/hooks/useEnhancedWavesList';
import { usePrefetchWaveData } from '@/hooks/usePrefetchWaveData';
import useDeviceInfo from '@/hooks/useDeviceInfo';
import { ApiWaveType } from '@/generated/models/ApiWaveType';
import { CollapsedWave } from './subcomponents/CollapsedWave';
import { ExpandedWave } from './subcomponents/ExpandedWave';
import { useWaveNavigation } from './hooks/useWaveNavigation';
import { useWaveNameTruncation } from './hooks/useWaveNameTruncation';
import { formatWaveName } from './utils/formatWaveName';
import { useMyStream } from '@/contexts/wave/MyStreamContext';
interface WebBrainLeftSidebarWaveProps {
  readonly wave: MinimalWave;
  readonly onHover: (waveId: string) => void;
  readonly showPin?: boolean | undefined;
  readonly basePath?: string | undefined;
  readonly collapsed?: boolean | undefined;
}
const TOOLTIP_PLACEMENT = 'right' as const;
const WebBrainLeftSidebarWave = t0 => {
  const $ = _c(40);
  const {
    wave,
    onHover,
    showPin: t1,
    basePath: t2,
    collapsed: t3
  } = t0;
  const showPin = t1 === undefined ? true : t1;
  const basePath = t2 === undefined ? "/waves" : t2;
  const collapsed = t3 === undefined ? false : t3;
  const {
    activeWave
  } = useMyStream();
  const searchParams = useSearchParams();
  const prefetchWaveData = usePrefetchWaveData();
  const {
    hasTouchScreen
  } = useDeviceInfo();
  let t4;
  if ($[0] !== activeWave.id || $[1] !== activeWave.set || $[2] !== basePath || $[3] !== hasTouchScreen || $[4] !== onHover || $[5] !== prefetchWaveData || $[6] !== searchParams || $[7] !== wave.firstUnreadDropSerialNo || $[8] !== wave.id) {
    t4 = {
      basePath,
      activeWaveId: activeWave.id,
      setActiveWave: activeWave.set,
      onHover,
      prefetchWaveData,
      searchParams,
      waveId: wave.id,
      hasTouchScreen,
      firstUnreadDropSerialNo: wave.firstUnreadDropSerialNo
    };
    $[0] = activeWave.id;
    $[1] = activeWave.set;
    $[2] = basePath;
    $[3] = hasTouchScreen;
    $[4] = onHover;
    $[5] = prefetchWaveData;
    $[6] = searchParams;
    $[7] = wave.firstUnreadDropSerialNo;
    $[8] = wave.id;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    href,
    isActive,
    onMouseEnter,
    onClick
  } = useWaveNavigation(t4);
  let t5;
  if ($[10] !== wave.name || $[11] !== wave.type) {
    t5 = formatWaveName({
      name: wave.name,
      type: wave.type
    });
    $[10] = wave.name;
    $[11] = wave.type;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const formattedWaveName = t5;
  let t6;
  if ($[13] !== collapsed || $[14] !== formattedWaveName) {
    t6 = {
      collapsed,
      contentKey: formattedWaveName
    };
    $[13] = collapsed;
    $[14] = formattedWaveName;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const {
    nameRef,
    isNameTruncated
  } = useWaveNameTruncation(t6);
  const isDropWave = wave.type !== ApiWaveType.Chat;
  const haveNewDrops = wave.newDropsCount.count > 0;
  const tooltipId = `wave-collapsed-${wave.id}`;
  const expandedTooltipId = `wave-expanded-${wave.id}`;
  const showCollapsedTooltip = collapsed && !hasTouchScreen;
  const showExpandedTooltip = !collapsed && !hasTouchScreen && isNameTruncated;
  if (collapsed) {
    let t7;
    if ($[16] !== formattedWaveName || $[17] !== haveNewDrops || $[18] !== href || $[19] !== isActive || $[20] !== isDropWave || $[21] !== onClick || $[22] !== onMouseEnter || $[23] !== showCollapsedTooltip || $[24] !== tooltipId || $[25] !== wave) {
      t7 = <CollapsedWave formattedWaveName={formattedWaveName} haveNewDrops={haveNewDrops} href={href} isActive={isActive} isDropWave={isDropWave} onMouseEnter={onMouseEnter} onClick={onClick} showTooltip={showCollapsedTooltip} tooltipId={tooltipId} tooltipPlacement={TOOLTIP_PLACEMENT} wave={wave} />;
      $[16] = formattedWaveName;
      $[17] = haveNewDrops;
      $[18] = href;
      $[19] = isActive;
      $[20] = isDropWave;
      $[21] = onClick;
      $[22] = onMouseEnter;
      $[23] = showCollapsedTooltip;
      $[24] = tooltipId;
      $[25] = wave;
      $[26] = t7;
    } else {
      t7 = $[26];
    }
    return t7;
  }
  let t7;
  if ($[27] !== expandedTooltipId || $[28] !== formattedWaveName || $[29] !== haveNewDrops || $[30] !== href || $[31] !== isActive || $[32] !== isDropWave || $[33] !== nameRef || $[34] !== onClick || $[35] !== onMouseEnter || $[36] !== showExpandedTooltip || $[37] !== showPin || $[38] !== wave) {
    t7 = <ExpandedWave formattedWaveName={formattedWaveName} haveNewDrops={haveNewDrops} href={href} isActive={isActive} isDropWave={isDropWave} latestDropTimestamp={wave.newDropsCount.latestDropTimestamp} nameRef={nameRef} onMouseEnter={onMouseEnter} onClick={onClick} showExpandedTooltip={showExpandedTooltip} showPin={showPin} tooltipContent={formattedWaveName} tooltipId={expandedTooltipId} tooltipPlacement={TOOLTIP_PLACEMENT} wave={wave} waveId={wave.id} isPinned={wave.isPinned} />;
    $[27] = expandedTooltipId;
    $[28] = formattedWaveName;
    $[29] = haveNewDrops;
    $[30] = href;
    $[31] = isActive;
    $[32] = isDropWave;
    $[33] = nameRef;
    $[34] = onClick;
    $[35] = onMouseEnter;
    $[36] = showExpandedTooltip;
    $[37] = showPin;
    $[38] = wave;
    $[39] = t7;
  } else {
    t7 = $[39];
  }
  return t7;
};
export default WebBrainLeftSidebarWave;